import {FeatureFlagValues, handleMatchRegExp, makeUserData} from "@buildwithflux/core";
import {
    ClientFirestoreAdapter,
    FirebaseUser,
    firstFromSnapshot,
    getFirestoreErrorCode,
} from "@buildwithflux/firestore-compatibility-layer";
import {
    LoginMethod,
    AuthenticationError,
    IUserData,
    PlanEpoch,
    TrialType,
    UsernameTakenError,
    UserUid,
    IUserPrivateMetadata,
    PlanVariantForEpochFrom202410,
    ExperimentConfiguration,
} from "@buildwithflux/models";
import {stableProbability} from "@buildwithflux/plans";
import {Logger, Unsubscriber, wait} from "@buildwithflux/shared";

import {FeatureFlagsStore} from "modules/feature_flags/FeatureFlagsStore";
import {experimentVariantFromConfiguration} from "modules/feature_flags/variants";

import {AuthService} from "./auth";

export interface SignupSuccess {
    firebaseUser: FirebaseUser;
    user: IUserData;
    userWasCreated: boolean;
}

interface UserCreationFeatureFlags {
    trialTypePaymentUpFrontRatio: number;
    paidCreditConsentRequired: ExperimentConfiguration | undefined;
}

/**
 * Service for creating user accounts
 *
 * A signUp is an operation that involves:
 *  - performFirebaseAuthenticationIfNeeded: An optional authentication step, where the user is logged in using
 *    Firebase Auth (linking any existing anonymous account on the way)
 *    - This step is skipped if the user is already logged in to Firebase Auth with a non-anonymous account
 *  - completeSignUpForAuthenticatedFirebaseUser: A transactional Firestore write, where the user's profile is created
 *    - This step is skipped if the user already has a profile in Firestore
 *    - The write involves 3 documents (users, usernames, usersPrivateMetadata), and one possible delete (old anonymous
 *      users document)
 */
export class SignUpService {
    private readonly featureUnsubscriber: Unsubscriber;
    private featureFlags: Partial<UserCreationFeatureFlags> = {};

    constructor(
        private readonly firebaseAuth: AuthService,
        private readonly firestoreAdapter: ClientFirestoreAdapter,
        featureFlagsStore: FeatureFlagsStore,
        private readonly logger: Logger,
    ) {
        this.featureUnsubscriber = featureFlagsStore.subscribe((flags) => {
            this.updateFromFeatureFlagsStore(flags);
        });
        this.updateFromFeatureFlagsStore(featureFlagsStore.getState());
    }

    public shutdown(): void {
        this.featureUnsubscriber();
    }

    /**
     * Always returns an authenticated Firebase user, ready for the next stage of registration
     */
    public async performFirebaseAuthenticationIfNeeded(loginMethod: LoginMethod): Promise<FirebaseUser> {
        let currentUser = this.firebaseAuth.getCurrentFirebaseUser();

        if (!currentUser || currentUser.isAnonymous) {
            const result = await this.firebaseAuth.getCredentialProvider(loginMethod, currentUser)();
            currentUser = result.user;
        }

        if (currentUser.isAnonymous) {
            // This should not happen, otherwise we failed to log in but didn't throw an error above
            throw new AuthenticationError("Failed to authenticate user during registration");
        }

        return currentUser;
    }

    /**
     * The return value indicates the user that was signed in, and whether their Flux user profile was created during
     * the current signUp call. This is useful for running one-time account-setup actions, such as cloning initial
     * documents.
     *
     * @param userHandle The handle that the user wants to use
     * @param firebaseUser The currently authenticated non-anonymous user
     */
    public async completeSignUpForAuthenticatedFirebaseUser(
        userHandle: string,
        firebaseUser: FirebaseUser,
    ): Promise<SignupSuccess> {
        // Validate the handle format before any Firestore operations to prevent invalid document paths
        // The handle is used as a Firestore document ID, so it cannot contain "/" or other invalid characters
        if (!handleMatchRegExp.test(userHandle)) {
            throw new AuthenticationError(
                "Invalid username format. Username may only contain alphanumeric characters or single hyphens, and cannot begin or end with a hyphen.",
            );
        }

        // Outside of the transaction, we query the current flux profile by uid - cannot query inside transaction
        const currentFluxProfile = firstFromSnapshot(
            await this.firestoreAdapter.userCollection().where("uid", "==", firebaseUser.uid).get(),
            () => undefined,
        );

        if (currentFluxProfile) {
            if (currentFluxProfile.isAnonymous) {
                // An existing anonymous user! Migrate their profile

                return {
                    firebaseUser,
                    user: await this.completeRegistrationByMigratingAnonymousUser(
                        firebaseUser,
                        currentFluxProfile,
                        userHandle,
                    ),
                    userWasCreated: true,
                };
            }

            // We successfully logged in during the registration process
            // But we should ignore the user's attempt to sign up again on an existing profile
            this.logger.debug("User was already logged in during registration attempt");
            return {
                user: currentFluxProfile,
                firebaseUser,
                userWasCreated: false,
            };
        }

        // A brand new user! Finish writing their profile
        return {
            firebaseUser,
            user: await this.completeRegistrationForNewUser(firebaseUser, userHandle),
            userWasCreated: true,
        };
    }

    public async completeRegistrationForNewUser(currentUser: FirebaseUser, userHandle: string): Promise<IUserData> {
        this.logger.debug("Creating brand new Flux profile");

        const newUser = makeUserData({
            uid: currentUser.uid,
            handle: userHandle.toLowerCase(),
            email: currentUser.email ?? undefined,
            full_name: currentUser.displayName ?? userHandle ?? "",
            isAnonymous: false,
            picture: currentUser.photoURL ?? "",
        });

        const user = await this.saveWithTransaction(newUser);
        await this.firebaseAuth.setUserHandleForFirebaseUser(currentUser, userHandle);

        try {
            await this.waitForLifecycleInitialized(user.uid);
        } catch (error) {
            this.logger.error("Failed to complete backend user setup during signup", error);
        }

        return user;
    }

    protected getCreationFlags(
        newUserUid: UserUid,
        newUserEmail: string | undefined,
    ): IUserPrivateMetadata["creationFlags"] {
        const planEpoch = this.getPlanEpoch(newUserEmail);
        const planVariant = this.getPlanVariant(planEpoch) ?? null;
        const trialType = this.getTrialType(newUserUid, planEpoch);
        const paidCreditConsentRequired = this.getPaidCreditConsentRequired(newUserUid);

        return {
            planEpoch,
            planVariant,
            trialType,
            paidCreditConsentRequired,
        };
    }

    protected async waitForLifecycleInitialized(userUid: UserUid, timeoutMs = 20_000): Promise<void> {
        const startTime = Date.now();

        while (Date.now() - startTime < timeoutMs) {
            try {
                const userPrivateMetadata = await this.firestoreAdapter.userPrivateMetadata(userUid).get();
                const lifecycle = userPrivateMetadata.data()?.lifecycle;

                if (lifecycle === "initialized") {
                    this.logger.debug("User lifecycle initialized successfully");
                    return;
                }

                if (lifecycle === "failed") {
                    throw new Error("User creation failed during backend processing");
                }

                await new Promise((resolve) => setTimeout(resolve, 500));
            } catch (error) {
                this.logger.error("Error checking lifecycle state", error);
                await new Promise((resolve) => setTimeout(resolve, 500));
            }
        }

        throw new Error("Timeout waiting for user lifecycle to be initialized");
    }

    private updateFromFeatureFlagsStore(flags: FeatureFlagValues) {
        this.featureFlags = {
            trialTypePaymentUpFrontRatio: flags.trialTypePaymentUpFrontRatio ?? 0,
            paidCreditConsentRequired: flags.paidCreditConsentRequired,
        };
    }

    /**
     * Formerly known as AuthenticationHelpers.makeNewUserDataWithPreviousUserData
     */
    private async completeRegistrationByMigratingAnonymousUser(
        currentUser: FirebaseUser,
        anonFluxUserProfile: IUserData,
        userHandle: string,
    ): Promise<IUserData> {
        this.logger.debug("Migrating existing anonymous Flux profile");

        const newUser = makeUserData({
            uid: currentUser.uid,
            handle: userHandle.toLowerCase(),
            email: currentUser.email ?? anonFluxUserProfile.email ?? undefined,
            full_name: currentUser.displayName ?? userHandle ?? "",
            isAnonymous: false,
            picture: currentUser.photoURL ?? anonFluxUserProfile.picture ?? "",
            sign_up_referrer: anonFluxUserProfile.sign_up_referrer ?? "",
            sign_up_page_referrer: anonFluxUserProfile.sign_up_page_referrer ?? "",
            documents_count: anonFluxUserProfile.documents_count ?? 0,
            parts_count: anonFluxUserProfile.parts_count ?? 0,
            star_count: anonFluxUserProfile.star_count ?? 0,
            created_at: anonFluxUserProfile.created_at ?? Date.now(),
            updated_at: Date.now(),
        });

        const user = await this.saveWithTransaction(
            {
                ...anonFluxUserProfile,
                ...newUser,
            },
            anonFluxUserProfile.handle,
        );
        await this.firebaseAuth.setUserHandleForFirebaseUser(currentUser, userHandle);

        try {
            await this.waitForLifecycleInitialized(user.uid);
        } catch (error) {
            this.logger.error("Failed to complete backend user setup during signup", error);
        }

        return user;
    }

    private getPlanEpoch(_newUserEmail: string | undefined): PlanEpoch {
        // All new users get the new plan epoch - educational users will see the Core plan with 100% discount
        return PlanEpoch.enum.from_202410;
    }

    private getPlanVariant(planEpoch: PlanEpoch): PlanVariantForEpochFrom202410 | undefined {
        if (planEpoch === PlanEpoch.enum.from_202410) {
            // New users now get the new variant of the pro/starter plans
            return PlanVariantForEpochFrom202410.enum.from_202504;
        }

        return undefined;
    }

    private getTrialType(newUserUid: UserUid, planEpoch: PlanEpoch): TrialType {
        if (planEpoch === PlanEpoch.enum.legacy) {
            return TrialType.enum.none;
        }

        if (stableProbability(newUserUid, this.featureFlags.trialTypePaymentUpFrontRatio ?? 0)) {
            return TrialType.enum.payment_details_up_front;
        } else {
            return TrialType.enum.reverse;
        }
    }

    /**
     * We want to ensure two things here:
     *  - That any existing user profile at the same handle as our write has the same UID as our write operation
     *    - This will be violated if some other process just managed to steal the username we wanted
     *  - That there is no existing user profile with the same handle, and a different UID
     *    - No existing user profile for the same handle is fine
     *    - An existing user profile for the same handle, with the same UID is fine (if this were violated, Firebase
     *      rules would fail anyway)
     *
     * Because there's no locking of rows that don't exist, we check both the user and handleMapping collections,
     * and perform a write in-between
     *
     * @param data The incoming user profile we are trying to write
     * @param existingAnonymousUserToRemoveHandle Handle of an existing anonymous user to remove, if any
     */
    private async saveWithTransaction(
        data: IUserData,
        existingAnonymousUserToRemoveHandle?: string,
    ): Promise<IUserData> {
        const {handle, uid, email} = data;
        const creationFlags = this.getCreationFlags(uid, email);

        this.logger.debug("Beginning transaction");
        return await this.firestoreAdapter.runTransaction(async (t): Promise<IUserData> => {
            const user = this.firestoreAdapter.user(handle);
            const anonUser = existingAnonymousUserToRemoveHandle
                ? this.firestoreAdapter.user(existingAnonymousUserToRemoveHandle)
                : undefined;
            const handleMapping = this.firestoreAdapter.handleMapping(handle);
            const userPrivateMetadata = this.firestoreAdapter.userPrivateMetadata(uid);

            const existingUserProfileByHandle = (await t.get(user)).data();
            const existingHandleMapping = (await t.get(handleMapping)).data();
            const existingAnonUserProfileByHandle = anonUser ? (await t.get(anonUser)).data() : undefined;

            let existingUserPrivateMetadata: IUserPrivateMetadata | undefined = undefined;

            try {
                existingUserPrivateMetadata = (await t.get(userPrivateMetadata)).data();
            } catch (error) {
                if (
                    getFirestoreErrorCode(error) === "permission-denied" ||
                    getFirestoreErrorCode(error) === "unknown"
                ) {
                    // Expected when the userPrivateMetadata does not exist
                    existingUserPrivateMetadata = undefined;
                } else {
                    throw error;
                }
            }

            // Indicates this username has been grabbed since the check in createRegistrationFlow()
            if (
                (existingUserProfileByHandle && existingUserProfileByHandle.uid !== uid) ||
                (existingHandleMapping && existingHandleMapping.user_uid !== uid)
            ) {
                throw new UsernameTakenError();
            }

            // Indicates something has changed since the check in createRegistrationFlow()
            if (existingAnonUserProfileByHandle && !existingAnonUserProfileByHandle.isAnonymous) {
                throw new AuthenticationError(
                    "Logic violation in transaction: did not expect non-anonymous user profile as previous anonymous user",
                );
            }

            if (existingUserProfileByHandle) {
                this.logger.debug({
                    existingUserProfileByHandle,
                    message: "Prepared to overwrite existing user profile",
                });
            } else {
                this.logger.debug({existingUserProfileByHandle, message: "No existing user profile to overwrite"});
            }

            if (existingAnonymousUserToRemoveHandle && existingAnonymousUserToRemoveHandle !== handle) {
                this.logger.debug("Will remove existing anonymous data: %s", existingAnonymousUserToRemoveHandle);
                t.delete(this.firestoreAdapter.user(existingAnonymousUserToRemoveHandle));

                // No need to delete their handleMapping, because anonymous users do not usually have a handleMapping/record in the usernames collection
                // No need to delete their userPrivateMetadata, because that collection is keyed by Firebase User ID, not handle, so will be overwritten already above
            }

            t.set(user, data);
            t.set(handleMapping, {
                user_uid: uid,
                userHandle: handle,
                organizationUid: undefined,
            });

            // We only get one chance to set the user private metadata creationFlags before falling foul of Firestore rules
            // So if the UPM already exists from a previous signup attempt, we can't overwrite it
            if (!existingUserPrivateMetadata?.creationFlags) {
                t.set(userPrivateMetadata, {uid, creationFlags}, {merge: true});
            }

            return data;
        });
    }

    private getPaidCreditConsentRequired(newUserUid: UserUid) {
        return experimentVariantFromConfiguration(
            newUserUid,
            this.featureFlags.paidCreditConsentRequired ?? {kind: "off"},
        );
    }
}

export class MockSignUpService extends SignUpService {
    constructor(
        firebaseAuth: AuthService,
        firestoreAdapter: ClientFirestoreAdapter,
        featureFlagsStore: FeatureFlagsStore,
        logger: Logger,
        public overrideCreationFlags?: IUserPrivateMetadata["creationFlags"],
    ) {
        super(firebaseAuth, firestoreAdapter, featureFlagsStore, logger);
    }

    protected override getCreationFlags(
        newUserUid: UserUid,
        newUserEmail: string | undefined,
    ): IUserPrivateMetadata["creationFlags"] {
        if (this.overrideCreationFlags) {
            return this.overrideCreationFlags;
        }
        return super.getCreationFlags(newUserUid, newUserEmail);
    }

    protected override async waitForLifecycleInitialized(_userUid: UserUid, _timeoutMs = 20_000): Promise<void> {
        await wait(2_000);
        return Promise.resolve();
    }
}
